import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface AssistantContextValue {
  isOpen: boolean
  seed: string | null
  openAssistant: (seed?: string) => void
  closeAssistant: () => void
  /** Alias for openAssistant — used by the command palette. */
  open: (seed?: string) => void
  clearSeed: () => void
}

const AssistantContext = createContext<AssistantContextValue | null>(null)

export const AssistantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [seed, setSeed] = useState<string | null>(null)

  const openAssistant = useCallback((nextSeed?: string) => {
    setSeed(nextSeed?.trim() || null)
    setIsOpen(true)
  }, [])

  const closeAssistant = useCallback(() => {
    setIsOpen(false)
  }, [])

  const clearSeed = useCallback(() => setSeed(null), [])

  const value = useMemo<AssistantContextValue>(
    () => ({
      isOpen,
      seed,
      openAssistant,
      closeAssistant,
      open: openAssistant,
      clearSeed,
    }),
    [isOpen, seed, openAssistant, closeAssistant, clearSeed],
  )

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>
}

export function useAssistant(): AssistantContextValue | null {
  return useContext(AssistantContext)
}
